export type FindReplaceMode = 'token_ci' | 'substring';

export type FindReplaceResult = {
  text: string;
  unmatched: string[];
};

export type SpanEditResult = {
  text: string;
  applied: boolean;
};

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const stringPairs = (pairs: Record<string, unknown>): Record<string, string> => {
  const next: Record<string, string> = {};
  for (const [key, value] of Object.entries(pairs)) {
    if (!key || typeof value !== 'string') continue;
    next[key] = value;
  }
  return next;
};

/**
 * Preview-only rewrite matching the backend rules:
 * - `token_ci`: consult note / session document — case-insensitive exact token
 * - `substring`: session context — case-sensitive, longer keys first, original-only
 */
export const applyFindReplace = (
  text: string,
  pairs: Record<string, unknown>,
  mode: FindReplaceMode
): FindReplaceResult => {
  const map = stringPairs(pairs);
  const keys = Object.keys(map).sort((left, right) => right.length - left.length);
  if (keys.length === 0) return { text, unmatched: [] };

  if (mode === 'substring') {
    const unmatched = keys.filter((key) => !text.includes(key));
    const pattern = new RegExp(keys.map(escapeRegExp).join('|'), 'g');
    return { text: text.replace(pattern, (match) => map[match] ?? match), unmatched };
  }

  const unmatched = keys.filter((key) => {
    const pattern = new RegExp(
      `(?<![\\p{L}\\p{N}_])${escapeRegExp(key)}(?![\\p{L}\\p{N}_])`,
      'iu'
    );
    return !pattern.test(text);
  });
  const pattern = new RegExp(
    keys
      .map((key) => `(?<![\\p{L}\\p{N}_])${escapeRegExp(key)}(?![\\p{L}\\p{N}_])`)
      .join('|'),
    'giu'
  );
  const rewritten = text.replace(pattern, (match) => {
    const found = keys.find((key) => key.toLowerCase() === match.toLowerCase());
    return found ? (map[found] ?? match) : match;
  });
  return { text: rewritten, unmatched };
};

export const findReplaceModeForTarget = (
  target: 'consult_note' | 'session_context' | 'session_document'
): FindReplaceMode => (target === 'session_context' ? 'substring' : 'token_ci');

/** Preview-only rewrite matching backend `apply_text_edit` (literal, case-sensitive). */
export const applySpanEdit = (
  text: string,
  oldString: string,
  newString: string,
  replaceAll: boolean
): SpanEditResult => {
  if (!oldString || oldString === newString) return { text, applied: false };
  const count = text.split(oldString).length - 1;
  if (count === 0) return { text, applied: false };
  if (count > 1 && !replaceAll) return { text, applied: false };
  return { text: text.split(oldString).join(newString), applied: true };
};
